/**
 * googleService.js
 * Módulo centralizado para Google OAuth2, Sheets API y Drive API.
 */

// ==========================================
// CONFIGURACIÓN DE CREDENCIALES
// ==========================================
const CLIENT_ID = '144761528674-1d1j9kf0s5en760g8olibl2pklkag7bp.apps.googleusercontent.com';
const API_KEY = ''; // Dejar vacío si no tienes una, OAuth funciona con el Token
const SCOPES = [
  'https://www.googleapis.com/auth/spreadsheets',
  'https://www.googleapis.com/auth/drive.file',
  'https://www.googleapis.com/auth/userinfo.email',
  'https://www.googleapis.com/auth/userinfo.profile'
].join(' ');

const SPREADSHEET_NAME_PREFIX = 'FondApp - Datos';
const DRIVE_FOLDER_NAME = 'FondApp - Comprobantes';

// Sheet names
const SHEETS = {
  BUDGETS: 'Presupuestos',
  EXPENSES: 'Gastos',
  MILESTONES: 'Hitos',
  ALERTS: 'Alertas',
  INVENTORY: 'Inventario',
  MOVEMENTS: 'Movimientos',
  CONFIG: 'Config'
};

let tokenClient = null;
let accessToken = null;
let gapiInitialized = false;
let spreadsheetId = null;
let driveFolderId = null;

// ─── GAPI Init ───────────────────────────────────────────────
export const initGapi = () => {
  return new Promise((resolve) => {
    if (gapiInitialized) { resolve(true); return; }
    
    if (!window.gapi) {
      console.error("Librería GAPI no cargada en index.html");
      resolve(false);
      return;
    }

    window.gapi.load('client', async () => {
      try {
        await window.gapi.client.init({
          apiKey: API_KEY,
          discoveryDocs: [
            'https://sheets.googleapis.com/$discovery/rest?version=v4',
            'https://www.googleapis.com/discovery/v1/apis/drive/v3/rest'
          ]
        });
        gapiInitialized = true;
        resolve(true);
      } catch (err) {
        console.error('Error inicializando GAPI:', err);
        resolve(false);
      }
    });
  });
};

// ─── OAuth2 Login ────────────────────────────────────────────
export const loginWithGoogle = () => {
  return new Promise((resolve, reject) => {
    try {
      if (!window.google || !window.google.accounts) {
        reject(new Error("Librería de Google Identity no cargada."));
        return;
      }

      tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: SCOPES,
        callback: async (tokenResponse) => {
          if (tokenResponse.error) {
            reject(new Error(tokenResponse.error));
            return;
          }
          accessToken = tokenResponse.access_token;
          
          // Establecer token para GAPI (Drive/Sheets)
          if (window.gapi && window.gapi.client) {
            window.gapi.client.setToken({ access_token: accessToken });
          }

          // Fetch user profile
          try {
            const res = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
              headers: { Authorization: `Bearer ${accessToken}` }
            });
            const profile = await res.json();
            
            const user = {
              name: profile.name,
              email: profile.email,
              picture: profile.picture,
              token: accessToken
            };

            resolve(user);
          } catch (err) {
            reject(err);
          }
        }
      });
      
      // Solicitar el token (esto abre el popup de Google)
      tokenClient.requestAccessToken({ prompt: 'consent' });
      
    } catch (err) {
      reject(err);
    }
  });
};

export const logout = () => {
  if (accessToken && window.google) {
    window.google.accounts.oauth2.revoke(accessToken);
    accessToken = null;
    spreadsheetId = null;
    driveFolderId = null;
  }
};

export const isAuthenticated = () => !!accessToken;

// ─── Drive: Folder Management ────────────────────────────────
const findOrCreateDriveFolder = async () => {
  if (driveFolderId) return driveFolderId;

  try {
    // Search for existing folder
    const searchRes = await window.gapi.client.drive.files.list({
      q: `name='${DRIVE_FOLDER_NAME}' and mimeType='application/vnd.google-apps.folder' and trashed=false`,
      spaces: 'drive',
      fields: 'files(id, name)'
    });

    if (searchRes.result.files && searchRes.result.files.length > 0) {
      driveFolderId = searchRes.result.files[0].id;
      return driveFolderId;
    }

    // Create new folder
    const createRes = await window.gapi.client.drive.files.create({
      resource: {
        name: DRIVE_FOLDER_NAME,
        mimeType: 'application/vnd.google-apps.folder'
      },
      fields: 'id'
    });
    driveFolderId = createRes.result.id;
    return driveFolderId;
  } catch (err) {
    console.error('Error con carpeta Drive:', err);
    return null;
  }
};

// ─── Drive: Upload File ──────────────────────────────────────
export const uploadFileToDrive = async (base64Data, fileName, mimeType = 'application/pdf') => {
  try {
    const folderId = await findOrCreateDriveFolder();
    
    // Extract actual base64 content (remove data:xxx;base64, prefix)
    const base64Content = base64Data.split(',')[1];
    const byteCharacters = atob(base64Content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    // Multipart upload
    const metadata = {
      name: fileName,
      parents: folderId ? [folderId] : []
    };

    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', blob);

    const res = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,webViewLink', {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      body: form
    });

    const data = await res.json();
    return { success: true, fileId: data.id, fileName: data.name, webViewLink: data.webViewLink };
  } catch (err) {
    console.error('Error subiendo archivo a Drive:', err);
    return { success: false, error: err.message };
  }
};

// ─── Sheets: Find or Create Spreadsheet ──────────────────────
export const findOrCreateSpreadsheet = async () => {
  if (spreadsheetId) return spreadsheetId;

  try {
    // Search for existing spreadsheet
    const searchRes = await window.gapi.client.drive.files.list({
      q: `name contains '${SPREADSHEET_NAME_PREFIX}' and mimeType='application/vnd.google-apps.spreadsheet' and trashed=false`,
      spaces: 'drive',
      fields: 'files(id, name)',
      orderBy: 'modifiedTime desc'
    });

    if (searchRes.result.files && searchRes.result.files.length > 0) {
      spreadsheetId = searchRes.result.files[0].id;
      // Ensure all sheets exist
      await ensureSheets();
      return spreadsheetId;
    }

    // Create new spreadsheet with all required sheets
    const createRes = await window.gapi.client.sheets.spreadsheets.create({
      resource: {
        properties: { title: SPREADSHEET_NAME_PREFIX },
        sheets: Object.values(SHEETS).map(name => ({
          properties: { title: name }
        }))
      }
    });

    spreadsheetId = createRes.result.spreadsheetId;

    // Add headers to each sheet
    await initializeSheetHeaders();

    return spreadsheetId;
  } catch (err) {
    console.error('Error con Spreadsheet:', err);
    return null;
  }
};

const ensureSheets = async () => {
  try {
    const res = await window.gapi.client.sheets.spreadsheets.get({ spreadsheetId });
    const existingNames = res.result.sheets.map(s => s.properties.title);
    const missing = Object.values(SHEETS).filter(name => !existingNames.includes(name));

    if (missing.length > 0) {
      await window.gapi.client.sheets.spreadsheets.batchUpdate({
        spreadsheetId,
        resource: {
          requests: missing.map(title => ({
            addSheet: { properties: { title } }
          }))
        }
      });
      await initializeSheetHeaders();
    }
  } catch (err) {
    console.error('Error verificando hojas:', err);
  }
};

const initializeSheetHeaders = async () => {
  const headers = {
    [SHEETS.BUDGETS]: [['Categoría', 'Aporte Institución', 'Contraparte Empresa', 'Gastado']],
    [SHEETS.EXPENSES]: [['ID', 'Fecha', 'Categoría', 'Descripción', 'Monto', 'Tipo Doc', 'Boleta/Factura DriveID', 'Comprobante DriveID', 'Timestamp']],
    [SHEETS.MILESTONES]: [['ID', 'Tipo', 'Título', 'Fecha', 'Descripción', 'Estado', 'Creado']],
    [SHEETS.ALERTS]: [['ID', 'Mensaje', 'Tipo', 'Categoría', 'Timestamp']],
    [SHEETS.INVENTORY]: [['ID', 'Código', 'Nombre', 'Categoría', 'Cantidad', 'Unidad', 'Stock Mínimo', 'Ubicación', 'Costo Unitario', 'Proveedor', 'Estado', 'Fecha Adquisición', 'Descripción', 'Creado']],
    [SHEETS.MOVEMENTS]: [['ID', 'Item ID', 'Código', 'Nombre', 'Tipo', 'Cantidad', 'Motivo', 'Responsable', 'Fecha', 'Timestamp']],
    [SHEETS.CONFIG]: [['Clave', 'Valor']]
  };

  const data = Object.entries(headers).map(([sheet, values]) => ({
    range: `${sheet}!A1`,
    values
  }));

  try {
    await window.gapi.client.sheets.spreadsheets.values.batchUpdate({
      spreadsheetId,
      resource: { valueInputOption: 'RAW', data }
    });
  } catch (err) {
    console.error('Error inicializando headers:', err);
  }
};

// ─── Sheets: Generic Read/Write ──────────────────────────────
const readSheet = async (sheetName) => {
  try {
    const ssId = await findOrCreateSpreadsheet();
    if (!ssId) return [];
    const res = await window.gapi.client.sheets.spreadsheets.values.get({
      spreadsheetId: ssId,
      range: `${sheetName}!A:Z`
    });
    const rows = res.result.values || [];
    return rows.length > 1 ? rows.slice(1) : []; // skip header
  } catch (err) {
    console.error(`Error leyendo ${sheetName}:`, err);
    return [];
  }
};

const appendToSheet = async (sheetName, values) => {
  try {
    const ssId = await findOrCreateSpreadsheet();
    if (!ssId) return false;
    await window.gapi.client.sheets.spreadsheets.values.append({
      spreadsheetId: ssId,
      range: `${sheetName}!A:Z`,
      valueInputOption: 'RAW',
      insertDataOption: 'INSERT_ROWS',
      resource: { values: [values] }
    });
    return true;
  } catch (err) {
    console.error(`Error escribiendo en ${sheetName}:`, err);
    return false;
  }
};

const writeSheet = async (sheetName, allRows) => {
  try {
    const ssId = await findOrCreateSpreadsheet();
    if (!ssId) return false;
    
    // Get existing header
    const headerRes = await window.gapi.client.sheets.spreadsheets.values.get({
      spreadsheetId: ssId,
      range: `${sheetName}!1:1`
    });
    const header = headerRes.result.values?.[0] || [];

    // Clear and rewrite
    await window.gapi.client.sheets.spreadsheets.values.clear({
      spreadsheetId: ssId,
      range: `${sheetName}!A:Z`
    });
    await window.gapi.client.sheets.spreadsheets.values.update({
      spreadsheetId: ssId,
      range: `${sheetName}!A1`,
      valueInputOption: 'RAW',
      resource: { values: [header, ...allRows] }
    });
    return true;
  } catch (err) {
    console.error(`Error reescribiendo ${sheetName}:`, err);
    return false;
  }
};

// ─── Budgets ─────────────────────────────────────────────────
export const saveBudgets = async (budgets) => {
  const rows = Object.entries(budgets).map(([cat, d]) => [
    cat, d.limitInstitucion, d.limitContraparte, d.spent
  ]);
  return await writeSheet(SHEETS.BUDGETS, rows);
};

export const loadBudgets = async () => {
  const rows = await readSheet(SHEETS.BUDGETS);
  const budgets = {};
  rows.forEach(r => {
    if (r[0]) {
      budgets[r[0]] = {
        limitInstitucion: parseFloat(r[1]) || 0,
        limitContraparte: parseFloat(r[2]) || 0,
        spent: parseFloat(r[3]) || 0
      };
    }
  });
  return budgets;
};

// ─── Expenses ────────────────────────────────────────────────
export const saveExpense = async (expense) => {
  // Upload files to Drive first
  let boletaDriveId = '', comprobanteDriveId = '';

  if (expense.boletaFactura) {
    const ext = expense.boletaFacturaName?.split('.').pop() || 'pdf';
    const fileName = `${expense.boletaFacturaType}_${expense.date}_${expense.id}.${ext}`;
    const res = await uploadFileToDrive(expense.boletaFactura, fileName);
    if (res.success) boletaDriveId = res.fileId;
  }

  if (expense.comprobantePago) {
    const ext = expense.comprobantePagoName?.split('.').pop() || 'pdf';
    const fileName = `comprobante_${expense.date}_${expense.id}.${ext}`;
    const res = await uploadFileToDrive(expense.comprobantePago, fileName);
    if (res.success) comprobanteDriveId = res.fileId;
  }

  return await appendToSheet(SHEETS.EXPENSES, [
    expense.id, expense.date, expense.category, expense.description,
    expense.amount, expense.boletaFacturaType,
    boletaDriveId, comprobanteDriveId, expense.timestamp
  ]);
};

export const loadExpenses = async () => {
  const rows = await readSheet(SHEETS.EXPENSES);
  return rows.map(r => ({
    id: parseInt(r[0]) || 0, date: r[1] || '', category: r[2] || '',
    description: r[3] || '', amount: parseFloat(r[4]) || 0,
    boletaFacturaType: r[5] || 'boleta',
    boletaDriveId: r[6] || '', comprobanteDriveId: r[7] || '',
    boletaFactura: r[6] ? true : null, comprobantePago: r[7] ? true : null,
    timestamp: r[8] || ''
  }));
};

// ─── Milestones ──────────────────────────────────────────────
export const saveMilestone = async (m) => {
  return await appendToSheet(SHEETS.MILESTONES, [
    m.id, m.type, m.title, m.date, m.description || '', m.status, m.createdAt
  ]);
};

export const loadMilestones = async () => {
  const rows = await readSheet(SHEETS.MILESTONES);
  return rows.map(r => ({
    id: parseInt(r[0]) || 0, type: r[1] || 'informe', title: r[2] || '',
    date: r[3] || '', description: r[4] || '', status: r[5] || 'pendiente',
    createdAt: r[6] || ''
  }));
};

export const updateMilestoneStatus = async (milestoneId, newStatus) => {
  const rows = await readSheet(SHEETS.MILESTONES);
  const updated = rows.map(r => {
    if (parseInt(r[0]) === milestoneId) { r[5] = newStatus; }
    return r;
  });
  return await writeSheet(SHEETS.MILESTONES, updated);
};

// ─── Alerts ──────────────────────────────────────────────────
export const saveAlert = async (alert) => {
  return await appendToSheet(SHEETS.ALERTS, [
    alert.id, alert.message, alert.type, alert.category, alert.timestamp
  ]);
};

export const loadAlerts = async () => {
  const rows = await readSheet(SHEETS.ALERTS);
  return rows.map(r => ({
    id: parseInt(r[0]) || 0, message: r[1] || '', type: r[2] || 'info',
    category: r[3] || '', timestamp: r[4] || ''
  }));
};

// ─── Inventory ───────────────────────────────────────────────
export const saveInventoryItem = async (item) => {
  return await appendToSheet(SHEETS.INVENTORY, [
    item.id, item.code, item.name, item.category, item.quantity, item.unit,
    item.minStock, item.location, item.acquisitionCost, item.supplier,
    item.status, item.acquisitionDate, item.description || '', item.createdAt
  ]);
};

export const loadInventory = async () => {
  const rows = await readSheet(SHEETS.INVENTORY);
  return rows.map(r => ({
    id: parseInt(r[0]) || 0, code: r[1] || '', name: r[2] || '',
    category: r[3] || 'otros', quantity: parseInt(r[4]) || 0,
    unit: r[5] || 'unidad', minStock: parseInt(r[6]) || 0,
    location: r[7] || '', acquisitionCost: parseFloat(r[8]) || 0,
    supplier: r[9] || '', status: r[10] || 'disponible',
    acquisitionDate: r[11] || '', description: r[12] || '',
    createdAt: r[13] || ''
  }));
};

export const updateInventoryItem = async (updatedItem) => {
  const rows = await readSheet(SHEETS.INVENTORY);
  const updated = rows.map(r => {
    if (parseInt(r[0]) === updatedItem.id) {
      return [
        updatedItem.id, updatedItem.code, updatedItem.name, updatedItem.category,
        updatedItem.quantity, updatedItem.unit, updatedItem.minStock, updatedItem.location,
        updatedItem.acquisitionCost, updatedItem.supplier, updatedItem.status,
        updatedItem.acquisitionDate, updatedItem.description || '', updatedItem.createdAt
      ];
    }
    return r;
  });
  return await writeSheet(SHEETS.INVENTORY, updated);
};

export const deleteInventoryItem = async (itemId) => {
  const rows = await readSheet(SHEETS.INVENTORY);
  const filtered = rows.filter(r => parseInt(r[0]) !== itemId);
  return await writeSheet(SHEETS.INVENTORY, filtered);
};

// ─── Movements ───────────────────────────────────────────────
export const saveMovement = async (mov) => {
  return await appendToSheet(SHEETS.MOVEMENTS, [
    mov.id, mov.itemId, mov.itemCode, mov.itemName, mov.type,
    mov.quantity, mov.reason || '', mov.responsible || '', mov.date, mov.timestamp
  ]);
};

export const loadMovements = async () => {
  const rows = await readSheet(SHEETS.MOVEMENTS);
  return rows.map(r => ({
    id: parseInt(r[0]) || 0, itemId: parseInt(r[1]) || 0,
    itemCode: r[2] || '', itemName: r[3] || '', type: r[4] || 'entrada',
    quantity: parseInt(r[5]) || 0, reason: r[6] || '',
    responsible: r[7] || '', date: r[8] || '', timestamp: r[9] || ''
  }));
};

// ─── Load All Data ───────────────────────────────────────────
export const loadAllData = async () => {
  try {
    await findOrCreateSpreadsheet();
    const [budgets, expenses, milestones, alerts, inventory, movements] = await Promise.all([
      loadBudgets(), loadExpenses(), loadMilestones(),
      loadAlerts(), loadInventory(), loadMovements()
    ]);
    return { success: true, budgets, expenses, milestones, alerts, inventory, movements };
  } catch (err) {
    console.error('Error cargando datos:', err);
    return { success: false };
  }
};